import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type {
  CreateOrUpdateEcsScheduledTaskRequest,
  EcsScheduledTaskDetail,
  EcsScheduledTaskSummary,
  EcsScheduledTasksAccount,
  LookupOption,
} from '@internal/plugin-ecs-scheduled-tasks-common';
import type { EcsScheduledTasksFrontendApi } from './types';
import { apiCache, createCacheKey } from '../utils';

function toQuery(params: Record<string, string | undefined>) {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v) usp.set(k, v);
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export class EcsScheduledTasksClient implements EcsScheduledTasksFrontendApi {
  constructor(
    private readonly options: {
      discoveryApi: DiscoveryApi;
      fetchApi: FetchApi;
    },
  ) {}

  private async baseUrl() {
    return this.options.discoveryApi.getBaseUrl('ecs-scheduled-tasks');
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const url = `${await this.baseUrl()}${path}`;
    const resp = await this.options.fetchApi.fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      ...init,
    });

    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      throw new Error(
        `Request failed: ${resp.status} ${resp.statusText}${
          text ? ` - ${text}` : ''
        }`,
      );
    }

    if (resp.status === 204) return undefined as T;
    return resp.json() as Promise<T>;
  }

  async listAccounts(): Promise<EcsScheduledTasksAccount[]> {
    return this.request<EcsScheduledTasksAccount[]>('/accounts', {
      method: 'GET',
    });
  }

  async listClusters(params: { accountId: string; region: string; provisionRoleArn?: string }): Promise<LookupOption[]> {
  return this.request<LookupOption[]>(
    `/lookups/clusters${toQuery(params)}`,
    { method: 'GET' },
  );
}

async listTaskFamilies(params: {
  accountId: string;
  region: string;
  provisionRoleArn?: string;
}): Promise<LookupOption[]> {
  return this.request<LookupOption[]>(
    `/lookups/task-families${toQuery(params)}`,
    { method: 'GET' },
  );
}

async listTaskRevisions(params: {
  accountId: string;
  region: string;
  family: string;
  provisionRoleArn?: string;
}): Promise<LookupOption[]> {
  return this.request<LookupOption[]>(
    `/lookups/task-revisions${toQuery(params)}`,
    { method: 'GET' },
  );
}

async getTaskDefinition(params: {
  accountId: string;
  region: string;
  taskDefinition: string;
  provisionRoleArn?: string;
}): Promise<any> {
  return this.request<any>(
    `/lookups/task-definition${toQuery(params)}`,
    { method: 'GET' },
  );
}

async listSubnets(params: {
  accountId: string;
  region: string;
  provisionRoleArn?: string;
}): Promise<LookupOption[]> {
  return this.request<LookupOption[]>(
    `/lookups/subnets${toQuery(params)}`,
    { method: 'GET' },
  );
}

async listSecurityGroups(params: {
  accountId: string;
  region: string;
  provisionRoleArn?: string;
}): Promise<LookupOption[]> {
  return this.request<LookupOption[]>(
    `/lookups/security-groups${toQuery(params)}`,
    { method: 'GET' },
  );
}

async listSchedules(params: {
  accountId: string;
  region: string;
  provisionRoleArn?: string;
  groupName?: string;
}, skipCache = false): Promise<EcsScheduledTaskSummary[]> {
  const cacheParams = {
    accountId: params.accountId,
    region: params.region,
    groupName: params.groupName || 'backstage-schedules',
  };
  const cacheKey = createCacheKey('listSchedules', cacheParams);

  // Check cache first (unless skipCache is true)
  if (!skipCache) {
    const cached = apiCache.get<EcsScheduledTaskSummary[]>(cacheKey);
    if (cached) {
      return cached;
    }
  }

  // Fetch from API
  const data = await this.request<EcsScheduledTaskSummary[]>(
    `/schedules${toQuery(params)}`,
    { method: 'GET' },
  );

  // Cache the result
  apiCache.set(cacheKey, data);

  return data;
}

invalidateCache(accountId: string, region: string, groupName?: string): void {
  const params = {
    accountId,
    region,
    groupName: groupName || 'backstage-schedules',
  };
  const key = createCacheKey('listSchedules', params);
  apiCache.delete(key);
}

clearCache(): void {
  apiCache.clear();
}

async getSchedule(params: {
  accountId: string;
  region: string;
  provisionRoleArn?: string;
  name: string;
  groupName?: string;
}): Promise<EcsScheduledTaskDetail> {
  const { name, ...rest } = params;
  return this.request<EcsScheduledTaskDetail>(
    `/schedules/${encodeURIComponent(name)}${toQuery(rest)}`,
    { method: 'GET' },
  );
}

  async createSchedule(
    payload: CreateOrUpdateEcsScheduledTaskRequest,
  ): Promise<EcsScheduledTaskDetail> {
    return this.request<EcsScheduledTaskDetail>('/schedules', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateSchedule(
    name: string,
    payload: CreateOrUpdateEcsScheduledTaskRequest,
  ): Promise<EcsScheduledTaskDetail> {
    return this.request<EcsScheduledTaskDetail>(
      `/schedules/${encodeURIComponent(name)}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    );
  }

async enableSchedule(params: {
  accountId: string;
  region: string;
  provisionRoleArn?: string;
  name: string;
  groupName?: string;
}): Promise<void> {
  const { name, ...rest } = params;
  await this.request<void>(
    `/schedules/${encodeURIComponent(name)}/enable${toQuery(rest)}`,
    { method: 'POST' },
  );
}

async disableSchedule(params: {
  accountId: string;
  region: string;
  provisionRoleArn?: string;
  name: string;
  groupName?: string;
}): Promise<void> {
  const { name, ...rest } = params;
  await this.request<void>(
    `/schedules/${encodeURIComponent(name)}/disable${toQuery(rest)}`,
    { method: 'POST' },
  );
}

async deleteSchedule(params: {
  accountId: string;
  region: string;
  provisionRoleArn?: string;
  name: string;
  groupName?: string;
}): Promise<void> {
  const { name, ...rest } = params;
  await this.request<void>(
    `/schedules/${encodeURIComponent(name)}${toQuery(rest)}`,
    { method: 'DELETE' },
  );
}
}
